<script setup>
import {useStore} from "vuex";
import {getActions} from "@/composables/useActions";
import {computed, onMounted, ref} from "vue";
import _ from "lodash";
import moment from "moment";
import CompletedActionsFilter from "@/components/actions/partials/CompletedActionsFilter.vue";
import GroupedActionPanel from "@/components/actions/partials/GroupedActionPanel.vue";
import ActionTime from "@/components/actions/partials/ActionTime.vue";
import ActionRow from "@/components/actions/partials/ActionRow.vue";
import CompletedActionsExport from "@/components/actions/partials/CompletedActionsExport.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import GroupedActionPanelTitle from "@/components/actions/partials/GroupedActionPanelTitle.vue";

const store = useStore();
const activeTab = computed(() => store.getters['actions/activeTab']);

const maxShowActions = 5;
const showAllMonth = ref();

const toggleShowAll = (month) => {
  showAllMonth.value = showAllMonth.value === month ? undefined : month;
}

const {data: completedActionsData, isLoading} = getActions(activeTab);

const actions = computed(() => completedActionsData.value?.actions ?? []);
const periods = computed(() => completedActionsData.value?.periods ?? []);

const completedByFilter = computed(() => {
  return _(store.getters['actions/completedByFilter'] ?? []).filter(filter => filter?.active).map(filter => filter.name).value();
});

const isCompletedByMatch = (action) => {
  // crawler must have completed the action
  if (!action.completed_by && !action.pending_completed_by) {
    return completedByFilter.value.includes(action.completed_by);
  }

  // its pending complete, not completed yet, so we check pending_completed_by user id
  if (action.pending_completed_by && !action.completed_by) {
    return completedByFilter.value.includes(action.pending_completed_by)
  }

  // otherwise we assume its been completed so we check completed_by
  return completedByFilter.value.includes(action.completed_by);
}

const groupedActions = computed(() =>
  periods.value.map((period) => {
    const monthActions = actions.value?.filter(action =>
      moment(action.pending_completed_at).isBetween(moment(period.start), moment(period.end))
      && isCompletedByMatch(action)
    );

    const showAll = showAllMonth.value === period.start;

    return {
      ...period,
      displayName: moment(period.start).format('Do MMM YYYY') + ' to ' + moment(period.end).format('Do MMM YYYY'),
      actions: showAll ? monthActions : monthActions.slice(0, maxShowActions),
      totalTime: monthActions?.map(action => action.action_time).reduce((a, b) => a + b, 0),
      totalActions: monthActions.length,
      moreActionsCount: Math.max(monthActions.length - maxShowActions, 0),
      showAll,
    }
  })
);

onMounted(() => {
  window.mixpanel('Actions v2: View completed');
})
</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-10">
    <CompletedActionsFilter />
    <LoadingBlock v-if="isLoading" />

    <GroupedActionPanel
      v-for="(group, key) in groupedActions"
      v-else
      :key="key"
    >
      <template #title>
        <GroupedActionPanelTitle :title="group.displayName" />
      </template>

      <template #action-time>
        <ActionTime
          :minutes="group.totalTime"
          class="tw-font-semibold"
        />
      </template>

      <template #toolbar>
        <CompletedActionsExport
          v-if="group.actions?.length > 0"
          :from-date="group.start"
          :to-date="group.end"
        />
      </template>

      <template #actions>
        <ActionRow
          v-for="action in group.actions"
          :key="action.id"
          :action="action"
        />
      </template>
      <template #zero-content>
        <div
          v-if="!isLoading && group.actions?.length === 0"
          class="tw-text-font tw-text-[1.2rem]"
        >
          No actions completed for this month
        </div>
      </template>
      <template #pagination>
        <div
          v-if="group.moreActionsCount > 0"
          class="tw-flex tw-justify-end tw-align-middle tw-gap-2 tw-p-4"
        >
          <div
            class="tw-flex-grow-0 tw-flex tw-align-middle tw-p-4 tw-gap-2"
          >
            <svg-vue
              class="tw-h-4 tw-w-4"
              icon="icons/bar-chart-2"
            />
            <span v-if="!group.showAll">
              {{
                `${group.moreActionsCount} more ${group.moreActionsCount === 1 ? 'action' : 'actions'} for this month`
              }}
            </span>
            <span v-else>
              {{
                `Showing ${group.totalActions} ${group.totalActions === 1 ? 'action' : 'actions'} for this month`
              }}
            </span>
          </div>
          <DefaultButton
            variant="primary-inverted"
            @click="toggleShowAll(group.start)"
          >
            {{ group.showAll ? 'View Less' : 'View All' }}
          </DefaultButton>
        </div>
      </template>
    </GroupedActionPanel>
  </div>
</template>
